import React, { createContext, useContext, useRef, useCallback, useMemo, useEffect, useState } from "react";
import useRestaurantWebSocket from "../../hooks/use-websocket";
import useMeshTransport, { isCWBrowser } from "../../hooks/useMeshTransport";
import { getPendingOrderNotifications, acknowledgeOrderNotification } from "../../services/orderServices";
import { getAccessToken } from "../../services/authInterceptor";

const WebSocketContext = createContext(null);

// How often the POS asks the database for online orders it has not been told
// about. This is the worst-case lateness of a popup when every live transport
// has failed.
const PENDING_ORDER_POLL_MS = 30000;

const WebSocketProvider = ({ children, url, bunitId, tillId, enabled, debug }) => {
  const useMesh = isCWBrowser();
  const rawListenersRef = useRef(new Set());
  const [meshAutoStarted, setMeshAutoStarted] = useState(false);

  const addRawMessageListener = useCallback((listener) => {
    rawListenersRef.current.add(listener);
    return () => rawListenersRef.current.delete(listener);
  }, []);

  const removeRawMessageListener = useCallback((listener) => {
    rawListenersRef.current.delete(listener);
  }, []);

  const handleMessage = useCallback((message) => {
    rawListenersRef.current.forEach((listener) => {
      try {
        listener(message);
      } catch (err) {
        console.error("[WebSocketProvider] Listener error:", err);
      }
    });
  }, []);

  // Auto-elect mesh role when running inside CW Browser (desktop only — mobile has no autoJoinOrHost).
  // The shell owns the decision (persisted role → reconnect-to-remembered-host →
  // LAN discovery → self-promote-to-host): see meshPlugin.js's autoJoinOrHost.
  // This used to unconditionally call mesh.startHost() here, which made every
  // device its own isolated host — orders placed on one till never reached
  // another's screen because there was never any attempt to discover or join
  // an existing host first.
  useEffect(() => {
    if (!useMesh || !enabled || meshAutoStarted) return;

    const autoStart = async () => {
      try {
        const mesh = window.electronAPI.mesh;

        if (typeof mesh.autoJoinOrHost === "function") {
          console.debug("[WebSocketProvider] CW Browser detected — resolving mesh role...");
          const status = await mesh.autoJoinOrHost();
          console.debug("[WebSocketProvider] Mesh role resolved:", status.mode);
        } else {
          const status = await mesh.getStatus();
          console.debug("[WebSocketProvider] Mesh mode:", status.mode);
        }
        setMeshAutoStarted(true);
      } catch (err) {
        setMeshAutoStarted(true);
        console.error("[WebSocketProvider] Mesh auto-election failed:", err);
      }
    };

    autoStart();
  }, [useMesh, enabled, meshAutoStarted]);

  const meshTransport = useMeshTransport({
    bunitId,
    tillId,
    enabled: useMesh && enabled,
    debug,
    onMessage: handleMessage,
  });

  const wsTransport = useRestaurantWebSocket({
    url,
    bunitId,
    tillId,
    enabled: !useMesh && enabled,
    debug,
    onMessage: handleMessage,
  });

  const transport = useMesh ? meshTransport : wsTransport;

  // ---------------------------------------------------------------------
  // Database catch-up for online orders.
  //
  // Lives HERE, above the mesh/websocket choice, because it is the one part
  // of this that must not depend on a transport at all. Putting it inside
  // useRestaurantWebSocket meant it was dead on every CW Browser till: those
  // run mesh, so that hook receives enabled:false and never starts. The
  // failure it exists for - no popup while orders book normally - is exactly
  // when the live transport cannot be trusted to tell us anything.
  //
  // Recovered orders are merged into visibleOrders alongside whatever the
  // active transport produced, so the popup renders through the same path
  // either way and App.js needs no changes.
  // ---------------------------------------------------------------------
  const [recoveredOrders, setRecoveredOrders] = useState([]);
  const seenOrderIdsRef = useRef(new Set());

  // Anything the live transport already showed must not be offered again by
  // the poll. Registering them here keeps one popup per order regardless of
  // which path won the race.
  useEffect(() => {
    (transport.visibleOrders || []).forEach((o) => {
      const id = o?.data?.orderId || o?.data?.externalId;
      if (id) seenOrderIdsRef.current.add(id);
    });
  }, [transport.visibleOrders]);

  const pollPendingOrders = useCallback(async () => {
    // tillId survives logout by design (till binding outlives the user
    // session — see useAuthStore.logout()), so this effect stays mounted and
    // ticking on /sign-in after logout. Without this check it fires an
    // unauthenticated GraphQL call every 30s, which 401s, which forces a
    // refresh attempt with no refreshToken, which hard-redirects to /sign-in
    // even when already there — a self-inflicted reload loop.
    if (!tillId || !getAccessToken()) return;
    try {
      const response = await getPendingOrderNotifications(tillId, bunitId, 20);
      const pending = response?.data?.data?.getPendingOrderNotifications;
      if (!Array.isArray(pending) || pending.length === 0) return;

      // Waiting more than two minutes means the live path is down and this
      // poll is carrying the popup on its own - worth saying out loud.
      const stale = pending.filter((p) => (p.ageSeconds || 0) > 120);
      if (stale.length > 0) {
        console.warn(
          `[WebSocketProvider] ${stale.length} online order(s) recovered by poll, not by ${
            useMesh ? "mesh" : "websocket"
          } - check transport health`
        );
      }

      const fresh = pending
        .filter((o) => {
          const id = o.sOrderId || o.externalId || o.orderNo;
          if (!id || seenOrderIdsRef.current.has(id)) return false;
          seenOrderIdsRef.current.add(id);
          return true;
        })
        .map((o) => ({
          data: {
            orderId: o.sOrderId,
            orderNo: o.orderNo,
            externalId: o.externalId,
            tillId: o.tillId,
            storeId: o.storeId,
            recoveredByPoll: true,
          },
        }));

      if (fresh.length > 0) {
        console.debug("[WebSocketProvider] Orders recovered by poll:", fresh.length);
        setRecoveredOrders((prev) => [...prev, ...fresh]);
      }
    } catch (err) {
      // A failed poll is not fatal - the next one is 30s away.
      console.error("[WebSocketProvider] Pending order poll failed:", err);
    }
  }, [tillId, bunitId, useMesh]);

  useEffect(() => {
    if (!tillId) return;
    pollPendingOrders();
    const timer = setInterval(pollPendingOrders, PENDING_ORDER_POLL_MS);
    return () => clearInterval(timer);
  }, [tillId, pollPendingOrders]);

  const visibleOrders = useMemo(
    () => [...(transport.visibleOrders || []), ...recoveredOrders],
    [transport.visibleOrders, recoveredOrders]
  );

  // Dismissing the popup is the operator confirming they have seen the order,
  // so it also retires it server-side. Without this the poll re-offers the
  // same order every 30s forever on every other device of the till.
  // Fire-and-forget: acknowledging is idempotent and the popup must close
  // whether or not the call succeeds.
  const acknowledgeOrder = useCallback(
    (orderId) => {
      if (!tillId || !orderId) return;
      acknowledgeOrderNotification(tillId, orderId).catch((err) =>
        console.error("[WebSocketProvider] Acknowledge failed:", err)
      );
    },
    [tillId]
  );

  const removeVisibleOrder = useCallback(
    (orderId) => {
      acknowledgeOrder(orderId);
      setRecoveredOrders((prev) => prev.filter((o) => o?.data?.orderId !== orderId));
      transport.removeVisibleOrder?.(orderId);
    },
    [acknowledgeOrder, transport.removeVisibleOrder]
  );

  const removeVisibleOrderByExternalId = useCallback(
    (externalId) => {
      setRecoveredOrders((prev) => {
        const match = prev.find((o) => o?.data?.externalId === externalId);
        if (match?.data?.orderId) acknowledgeOrder(match.data.orderId);
        return prev.filter((o) => o?.data?.externalId !== externalId);
      });
      transport.removeVisibleOrderByExternalId?.(externalId);
    },
    [acknowledgeOrder, transport.removeVisibleOrderByExternalId]
  );

  const value = useMemo(
    () => ({
      connected: transport.connected,
      reconnecting: transport.reconnecting,
      lastMessage: transport.lastMessage,
      // Merged: live transport orders plus anything the database poll
      // recovered. Consumers see one list and cannot tell which path a given
      // order arrived by.
      visibleOrders,
      orderUpdateFlag: transport.orderUpdateFlag,
      subscribe: transport.subscribe,
      // Wrapped so dismissing also acknowledges server-side and clears the
      // recovered list, not just the transport's own queue.
      removeVisibleOrder,
      removeVisibleOrderByExternalId,
      sendMessage: transport.sendMessage,
      connect: transport.connect,
      disconnect: transport.disconnect,
      reconnect: transport.reconnect,
      addRawMessageListener,
      removeRawMessageListener,
      isMesh: useMesh,
      sendMeshEvent: useMesh ? meshTransport.sendMeshEvent : null,
      transportMode: useMesh ? "mesh" : "websocket",
    }),
    [
      transport.connected,
      transport.reconnecting,
      transport.lastMessage,
      visibleOrders,
      transport.orderUpdateFlag,
      transport.subscribe,
      removeVisibleOrder,
      removeVisibleOrderByExternalId,
      transport.sendMessage,
      transport.connect,
      transport.disconnect,
      transport.reconnect,
      addRawMessageListener,
      removeRawMessageListener,
      useMesh,
      meshTransport.sendMeshEvent,
    ]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return ctx;
};

export { WebSocketProvider, useWebSocket };
